/**
 * Overlay-Varianten (4:3 / 1:1).
 *
 * Der Overlay-Builder erzeugt pro Overlay ZWEI Branding-Assets: eine 4:3- und
 * eine 1:1-Variante. Ihre Dateinamen (und damit `BrandingAsset.name`) teilen
 * denselben Basis-Namen und unterscheiden sich nur im Aspekt-Token, z. B.
 *   overlay-bar-4x3-1720000000000
 *   overlay-bar-1x1-1720000000000
 *
 * Ziel:
 *  - In Auswahl-Ansichten NUR eine Variante zeigen (bevorzugt 4:3, Default).
 *  - Beim Anwenden automatisch die zum Ziel-Seitenverhältnis passende Variante
 *    verwenden (Default 4:3).
 *
 * Einzel-Overlays (manuell hochgeladen, ohne Aspekt-Token) werden unverändert
 * behandelt — sie sind ihre eigene „Gruppe".
 */

import type { BrandingAsset, AspectRatio } from '@/types';

const ASPECT_TOKEN = /-(4x3|1x1)-/;

/** Numerischer Wert (Breite/Höhe) je Seitenverhältnis. */
export const ASPECT_VALUE: Record<AspectRatio, number> = { '4:3': 4 / 3, '1:1': 1 };

/** Nächstliegendes unterstütztes Seitenverhältnis (4:3 oder 1:1) zu einem Ratio. */
export function classifyAspect(ratio: number): AspectRatio {
  return Math.abs(ratio - ASPECT_VALUE['4:3']) <= Math.abs(ratio - ASPECT_VALUE['1:1']) ? '4:3' : '1:1';
}

/** Aspekt-Token für Dateinamen ('4x3' | '1x1'). */
export function aspectToken(aspect: AspectRatio): '4x3' | '1x1' {
  return aspect === '1:1' ? '1x1' : '4x3';
}

/** Aspekt aus dem Asset-Namen, oder null bei Einzel-Overlays ohne Token. */
export function overlayAspectOf(name: string): AspectRatio | null {
  const m = name.match(ASPECT_TOKEN);
  if (!m) return null;
  return m[1] === '1x1' ? '1:1' : '4:3';
}

/** Gemeinsamer Gruppen-Schlüssel aller Aspekt-Varianten eines Overlays. */
export function overlayGroupKey(name: string): string {
  return name.replace(ASPECT_TOKEN, '-');
}

/**
 * Reduziert die rohe Overlay-Liste auf die Auswahl-Liste: pro logischer Overlay
 * genau EIN Eintrag — bevorzugt die 4:3-Variante. Einzel-Overlays bleiben
 * erhalten. Die ursprüngliche Reihenfolge (nach createdAt) bleibt bestehen.
 */
export function overlaysForSelection(assets: BrandingAsset[]): BrandingAsset[] {
  const groupPrimaryId = new Map<string, string>(); // groupKey → gewählte Asset-ID
  for (const a of assets) {
    const aspect = overlayAspectOf(a.name);
    if (!aspect) continue; // Einzel-Overlay: immer behalten
    const key = overlayGroupKey(a.name);
    const currentId = groupPrimaryId.get(key);
    if (!currentId) {
      groupPrimaryId.set(key, a.id);
      continue;
    }
    // 4:3 gewinnt gegen andere Aspekte.
    if (aspect === '4:3') groupPrimaryId.set(key, a.id);
  }
  const keep = new Set(groupPrimaryId.values());
  return assets.filter((a) => overlayAspectOf(a.name) === null || keep.has(a.id));
}

/**
 * Passende Overlay-URL für ein Ziel-Seitenverhältnis. Für die ausgewählte
 * (i. d. R. 4:3-)Variante wird die Geschwister-Variante des Ziel-Aspekts gesucht.
 * Fällt auf die ausgewählte Variante zurück (Default 4:3). Einzel-Overlays ohne
 * Token bleiben unverändert.
 */
export function resolveOverlayUrlForAspect(
  selected: BrandingAsset | null | undefined,
  allAssets: BrandingAsset[],
  targetAspect: AspectRatio,
): string | null {
  if (!selected) return null;
  if (overlayAspectOf(selected.name) === null) return selected.publicUrl;
  const key = overlayGroupKey(selected.name);
  const match = allAssets.find(
    (a) => overlayGroupKey(a.name) === key && overlayAspectOf(a.name) === targetAspect,
  );
  return (match ?? selected).publicUrl;
}

/**
 * Alle Assets derselben Overlay-Gruppe (4:3 + 1:1). Einzel-Overlays ohne Token
 * bilden eine Gruppe für sich (nur sie selbst).
 */
export function overlayGroupMembers(asset: BrandingAsset, allAssets: BrandingAsset[]): BrandingAsset[] {
  if (overlayAspectOf(asset.name) === null) return [asset];
  const key = overlayGroupKey(asset.name);
  return allAssets.filter((a) => overlayGroupKey(a.name) === key);
}

/** Vorhandene Aspekte in der Overlay-Gruppe (aus den Namen). */
export function overlayGroupAspects(asset: BrandingAsset, allAssets: BrandingAsset[]): AspectRatio[] {
  const aspects = overlayGroupMembers(asset, allAssets)
    .map((m) => overlayAspectOf(m.name))
    .filter((a): a is AspectRatio => a !== null);
  return [...new Set(aspects)];
}

/**
 * True, wenn das Overlay KEINE Variante hat, die zum Ziel-Seitenverhältnis passt
 * (relative Toleranz, Default 5 %) — dann würde es beim Anwenden verzerrt.
 * Overlays ohne Aspekt-Info (Alt-Uploads) lösen keine Warnung aus.
 */
export function overlayNeedsAspectWarning(
  selected: BrandingAsset | null | undefined,
  allAssets: BrandingAsset[],
  targetRatio: number,
  tolerance = 0.05,
): boolean {
  if (!selected) return false;
  const aspects = overlayGroupAspects(selected, allAssets);
  if (aspects.length === 0) return false;
  return !aspects.some((a) => Math.abs(targetRatio - ASPECT_VALUE[a]) / ASPECT_VALUE[a] <= tolerance);
}
